import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Paper,
  alpha
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { MAX_CHAR_LENGTH } from '../utils';

type Props = {
  title: string;
  valuesLabel: string;
  valuesPlaceholder: string;
  valuesHelper: string;
  requiredLabel: string;
  values: string;
  required: boolean;
  errorKey?: string;
  onValuesChange: (value: string) => void;
  onRequiredChange: (value: boolean) => void;
};

export const AssessmentFieldConfig = ({
  title,
  valuesLabel,
  valuesPlaceholder,
  valuesHelper,
  requiredLabel,
  values,
  required,
  errorKey,
  onValuesChange,
  onRequiredChange
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const charCount = values.length;
  const isOverLimit = charCount > MAX_CHAR_LENGTH;

  const getErrorMessage = (key: string | undefined): string | undefined => {
    if (!key) return undefined;
    return t(`settings:equipmentAssessment.validation.${key}`, {
      maxLength: MAX_CHAR_LENGTH
    });
  };

  const errorMessage = getErrorMessage(errorKey);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper
      }}
    >
      <Typography
        variant="h6"
        component="h2"
        sx={{
          fontWeight: 600,
          mb: 3,
          color: theme.palette.text.primary
        }}
      >
        {title}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label={valuesLabel}
          placeholder={valuesPlaceholder}
          value={values}
          onChange={e => onValuesChange(e.target.value)}
          error={!!errorMessage || isOverLimit}
          helperText={errorMessage || valuesHelper}
          InputProps={{
            sx: {
              fontFamily: 'inherit'
            }
          }}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 0.5
          }}
        >
          <Typography
            variant="caption"
            color={isOverLimit ? 'error' : 'text.secondary'}
          >
            {charCount}/{MAX_CHAR_LENGTH}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          borderRadius: 1
        }}
      >
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {requiredLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {required
              ? t('settings:equipmentAssessment.requiredHelper.mandatory')
              : t('settings:equipmentAssessment.requiredHelper.optional')}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={required}
              onChange={e => onRequiredChange(e.target.checked)}
              color="primary"
            />
          }
          label={
            required
              ? t('settings:equipmentAssessment.toggle.mandatory')
              : t('settings:equipmentAssessment.toggle.optional')
          }
          labelPlacement="start"
          sx={{
            ml: 2,
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
              fontWeight: 500,
              color: required
                ? theme.palette.primary.main
                : theme.palette.text.secondary
            }
          }}
        />
      </Box>
    </Paper>
  );
};
